import moment from 'moment';
import { App } from '@capacitor/app';
import { IAuthorityErrata, ErrataProvider } from '@autopylot-internal/tiles-client';
import { State } from '@meraki-internal/state';
import { Feature } from '@turf/turf';
import { IAuthorizableGeometry, IAuthorizableProperties } from '@autopylot-internal/tiles-client';
import { Logger } from '../support/debug/Logger';

// we poll every minute even though it has a 5m cache
// we do this b/c it does little harm (we just hit the local http cache most of the time)
// and then we end up with a worst case of 6m til we see the new errata (5m for the cache
// to bust and 1m for our polling)
// NOTE: This polling logic is currently duplicated in CachedErrataProvider in tiles-client
const POLL_EVERY_MS = 1 * 60 * 1000; // 1 min

// how long to allow errata to fail before logging error to sentry
const LOG_ERROR_AFTER_MS = 10 * 60 * 1000; // 10 mins

export class ErrataState extends State<{}> {
    static inject = () => [ErrataProvider, Logger];
    constructor(private errataProvider: ErrataProvider, private log: Logger){
        super({});
        this.resetTimestamps();
    }

    private currentErrata: IAuthorityErrata | undefined;

    private errataTimestamp = Date.now(); // when we last successfully retrieved errata
    private logTimestamp = Date.now(); // when we last logged an error about failed errata

    private resetTimestamps = () => {
        this.errataTimestamp = Date.now();
        this.logTimestamp = Date.now();
    };

    private syncLocalErrataFromRemote = async () => {
        // do nothing if we're in the background
        const { isActive } = await App.getState();
        if (!isActive) {
            this.resetTimestamps();
            return;
        }

        try{
            const errata = await this.errataProvider.getErrata();

            if (!this.currentErrata || JSON.stringify(this.currentErrata) !== JSON.stringify(errata)){
                this.currentErrata = errata;
                this.resetTimestamps();
                this.setState({});
            }
        }
        catch(err){
            if (Date.now() - this.logTimestamp > LOG_ERROR_AFTER_MS) {

                const errataAge = moment().diff(moment(this.errataTimestamp), 'm');
                const wrappedError = new Error(`failed to fetch errata for ${errataAge} mins. Inner Error: ${(err as any).toString()}`);

                // copy over the error code, in case it is fetch.failed which sentry knows to ignore
                (wrappedError as any).errorCode = (err as any).errorCode;

                this.log.error(wrappedError);

                this.logTimestamp = Date.now();
            }
        }
    };

    pollForErrata = async () => {
        while (true){
            await this.syncLocalErrataFromRemote();
            await new Promise(resolve => setTimeout(resolve, POLL_EVERY_MS));
        }
    };

    getRemoveIds = (): string[] => {
        if (!this.currentErrata){
            return [];
        }
        return this.currentErrata.removes.map(r => r.GLOBAL_ID).concat(this.currentErrata.changes.map(f => f.properties!.GLOBAL_ID));
    };

    getErrataFeatures = (): Feature<IAuthorizableGeometry, IAuthorizableProperties>[] => {
        if (!this.currentErrata){
            return [];
        }
        return this.currentErrata.adds.concat(this.currentErrata.changes);
    };
}
